import type { Transaction } from "@/api/schemas/transaction.schema";

export type DuplicateConfidence = "high" | "medium";

export interface DuplicateTransactionGroup {
  id: string;
  type: Transaction["type"];
  confidence: DuplicateConfidence;
  score: number;
  dateSpanDays: number;
  amountSpan: number;
  reasons: string[];
  transactions: Transaction[];
}

interface PairMatch {
  a: Transaction;
  b: Transaction;
  confidence: DuplicateConfidence;
  score: number;
  reasons: string[];
}

export interface DuplicateDetectionOptions {
  dayWindow?: number;
  amountTolerance?: number;
  minLabelSimilarity?: number;
}

const DEFAULT_DAY_WINDOW = 3;
const DEFAULT_AMOUNT_TOLERANCE = 0.5;
const DEFAULT_MIN_LABEL_SIMILARITY = 0.55;

function normalizeLabel(value?: string | null) {
  return (value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .trim();
}

function labelTokens(value?: string | null) {
  return normalizeLabel(value)
    .split(" ")
    .filter((token) => token.length >= 2);
}

function labelSimilarity(a?: string | null, b?: string | null) {
  const left = new Set(labelTokens(a));
  const right = new Set(labelTokens(b));
  if (left.size === 0 || right.size === 0) return 0;

  let intersection = 0;
  for (const token of left) {
    if (right.has(token)) intersection += 1;
  }

  const union = new Set([...left, ...right]).size;
  return union === 0 ? 0 : intersection / union;
}

function utcDay(value: string) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate());
}

function dayDistance(a: Transaction, b: Transaction) {
  const left = utcDay(a.date);
  const right = utcDay(b.date);
  if (left === null || right === null) return Number.POSITIVE_INFINITY;
  return Math.abs(left - right) / 86_400_000;
}

function transactionAmount(transaction: Transaction) {
  return Math.abs(Number(transaction.amount || 0));
}

function transactionCurrency(transaction: Transaction) {
  return (transaction.currency || "EUR").toUpperCase();
}

function transactionOwner(transaction: Transaction) {
  return transaction.paid_by || transaction.user?.id || transaction.from_user?.id || "";
}

function sameComparableType(a: Transaction, b: Transaction) {
  if (a.type !== b.type) return false;
  if (a.type === "pool_event" && a.event_type !== b.event_type) return false;
  return true;
}

function pairMatch(
  a: Transaction,
  b: Transaction,
  options: Required<DuplicateDetectionOptions>
): PairMatch | null {
  if (a.id === b.id) return null;
  if (!sameComparableType(a, b)) return null;
  if (transactionCurrency(a) !== transactionCurrency(b)) return null;

  const days = dayDistance(a, b);
  if (days > options.dayWindow) return null;

  const amountDiff = Math.abs(transactionAmount(a) - transactionAmount(b));
  if (amountDiff > Math.max(options.amountTolerance, 1)) return null;

  const similarity = labelSimilarity(a.description, b.description);
  const sameCategory = Boolean(a.category?.id && a.category?.id === b.category?.id);
  const sameOwner = Boolean(transactionOwner(a) && transactionOwner(a) === transactionOwner(b));
  const exactAmount = amountDiff <= 0.01;
  const nearAmount = amountDiff <= options.amountTolerance;

  const strongAmountDate = exactAmount && days <= 1 && (similarity >= 0.35 || sameCategory || sameOwner);
  const strongLabelDate = nearAmount && days <= 1 && similarity >= options.minLabelSimilarity;
  const widerWindow = exactAmount && days <= options.dayWindow && similarity >= options.minLabelSimilarity;

  if (!strongAmountDate && !strongLabelDate && !widerWindow) return null;

  const score = Math.round(
    35
    + (exactAmount ? 25 : Math.max(0, 20 - amountDiff * 20))
    + Math.max(0, 20 - days * 5)
    + similarity * 15
    + (sameCategory ? 5 : 0)
  );

  const reasons = [
    exactAmount ? "Same amount" : `Amounts differ by ${amountDiff.toFixed(2)}`,
    days === 0 ? "Same date" : `${days} day${days === 1 ? "" : "s"} apart`,
  ];

  if (similarity >= options.minLabelSimilarity) reasons.push("Similar labels");
  if (sameCategory) reasons.push("Same category");
  if (sameOwner) reasons.push("Same person");

  return {
    a,
    b,
    confidence: score >= 82 ? "high" : "medium",
    score,
    reasons,
  };
}

class DisjointSet {
  private parents = new Map<string, string>();

  add(value: string) {
    if (!this.parents.has(value)) this.parents.set(value, value);
  }

  find(value: string): string {
    const parent = this.parents.get(value);
    if (!parent || parent === value) return value;
    const root = this.find(parent);
    this.parents.set(value, root);
    return root;
  }

  union(a: string, b: string) {
    this.add(a);
    this.add(b);
    const left = this.find(a);
    const right = this.find(b);
    if (left !== right) this.parents.set(right, left);
  }
}

export function findDuplicateTransactions(
  transactions: Transaction[],
  options: DuplicateDetectionOptions = {}
): DuplicateTransactionGroup[] {
  const resolvedOptions = {
    dayWindow: options.dayWindow ?? DEFAULT_DAY_WINDOW,
    amountTolerance: options.amountTolerance ?? DEFAULT_AMOUNT_TOLERANCE,
    minLabelSimilarity: options.minLabelSimilarity ?? DEFAULT_MIN_LABEL_SIMILARITY,
  };

  const candidates = transactions.filter((transaction) => transaction.date && Number.isFinite(transactionAmount(transaction)));
  const matches: PairMatch[] = [];
  const sets = new DisjointSet();

  for (let i = 0; i < candidates.length; i += 1) {
    for (let j = i + 1; j < candidates.length; j += 1) {
      const match = pairMatch(candidates[i], candidates[j], resolvedOptions);
      if (!match) continue;
      matches.push(match);
      sets.union(match.a.id, match.b.id);
    }
  }

  const groups = new Map<string, Transaction[]>();
  for (const match of matches) {
    const root = sets.find(match.a.id);
    groups.set(root, groups.get(root) || []);
    const group = groups.get(root)!;
    if (!group.some((transaction) => transaction.id === match.a.id)) group.push(match.a);
    if (!group.some((transaction) => transaction.id === match.b.id)) group.push(match.b);
  }

  return [...groups.entries()]
    .map(([root, groupTransactions]) => {
      const groupMatches = matches.filter((match) => sets.find(match.a.id) === root);
      const dates = groupTransactions
        .map((transaction) => utcDay(transaction.date))
        .filter((value): value is number => value !== null);
      const amounts = groupTransactions.map(transactionAmount);
      const bestScore = Math.max(...groupMatches.map((match) => match.score));
      const reasons = [...new Set(groupMatches.flatMap((match) => match.reasons))];

      return {
        id: root,
        type: groupTransactions[0].type,
        confidence: groupMatches.some((match) => match.confidence === "high") ? "high" : "medium",
        score: bestScore,
        dateSpanDays: dates.length > 0 ? (Math.max(...dates) - Math.min(...dates)) / 86_400_000 : 0,
        amountSpan: amounts.length > 0 ? Math.max(...amounts) - Math.min(...amounts) : 0,
        reasons,
        transactions: groupTransactions.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()),
      } satisfies DuplicateTransactionGroup;
    })
    .sort((a, b) => {
      if (a.confidence !== b.confidence) return a.confidence === "high" ? -1 : 1;
      return b.score - a.score;
    });
}
